import React, { useState, useEffect } from "react";
import { IconRobot, IconPlayerPlay, IconPlayerPause, IconChartBar, IconMapPin, IconBuilding, IconBrain, IconInbox, IconSpeakerphone } from "@tabler/icons-react";
import { Table, Button, Badge, Group, Text, Title, Paper, ActionIcon } from '@mantine/core';

const AGENT_META = {
  "MAP_DISCOVERY": { name: "Google Maps Auto Discovery", icon: IconMapPin, color: "blue", desc: "Szuka firm korzystając z płatnego Google Maps API." },
  "OSM_DISCOVERY": { name: "OpenStreetMap Auto Discovery", icon: IconMapPin, color: "cyan", desc: "Szuka firm w darmowym Nominatim (OSM) w tle." },
  "CEIDG_DISCOVERY": { name: "CEIDG Auto Discovery", icon: IconBuilding, color: "orange", desc: "Pobiera najnowsze wpisy z rejestru CEIDG o 17:00." },
  "AI_ENRICHMENT": { name: "AI Lead Enrichment", icon: IconBrain, color: "grape", desc: "Wyszukuje strony www i emaile oraz używa LLM." },
  "INBOX_MONITOR": { name: "Inbox Monitor & Responder", icon: IconInbox, color: "teal", desc: "Skanuje maile przychodzące i generuje wersje robocze odpowiedzi." },
  "OUTREACH_CAMPAIGN": { name: "Outreach Campaign Agent", icon: IconSpeakerphone, color: "red", desc: "Wysyła spersonalizowane wiadomości e-mail w aktywnych kampaniach." },
  "GENERATE_AD_CAMPAIGN": { name: "Generowanie Kampanii Reklamowych (AI)", icon: IconRobot, color: "indigo", desc: "Asystent AI (Agentic AI) automatycznie analizuje cennik i tworzy kampanie Google/Facebook Ads w tle." }
};
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '';

const AgentManager = () => {
  const [agents, setAgents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchAgents();
  }, []);

  const getAuthHeader = () => {
    const token = localStorage.getItem("token");
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const fetchAgents = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/agents`, { headers: getAuthHeader() });
      if (!res.ok) throw new Error("Server returned " + res.status);
      const data = await res.json();
      setAgents(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error(err);
      setAgents([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleAgent = async (agentType, currentState) => {
    try {
      await fetch(`${API_BASE_URL}/api/agents/${agentType}/toggle?enabled=${!currentState}`, { 
        method: 'POST',
        headers: getAuthHeader()
      });
      fetchAgents();
    } catch (err) {
      console.error(err);
    }
  };

  if (loading) {
    return <div className="p-8 text-center text-gray-500">Ładowanie statusu agentów...</div>;
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <Group mb="lg">
        <IconRobot size={32} color="#4f46e5" />
        <Title order={2} style={{ color: '#1f2937' }}>Panel Kontroli Agentów AI</Title>
      </Group>
      
      <Text c="dimmed" mb="xl">
        Zarządzaj autonomicznymi procesami działającymi w tle systemu. Zobacz ich dzisiejsze statystyki i kontroluj ich zachowanie.
      </Text>

      <Paper shadow="sm" radius="md" withBorder>
        <Table highlightOnHover verticalSpacing="sm">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Agent</Table.Th>
              <Table.Th>Opis</Table.Th>
              <Table.Th style={{ textAlign: 'center' }}>Akcje Dzisiaj</Table.Th>
              <Table.Th style={{ textAlign: 'center' }}>Status</Table.Th>
              <Table.Th style={{ textAlign: 'right' }}>Kontrola</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {agents.map((agent) => {
              const meta = AGENT_META[agent.agentType] || { name: agent.agentType, icon: IconRobot, color: "gray", desc: "Nieznany agent" };
              const Icon = meta.icon;
              
              return (
                <Table.Tr key={agent.agentType} style={{ opacity: agent.enabled ? 1 : 0.7 }}>
                  <Table.Td>
                    <Group gap="sm" wrap="nowrap">
                      <ActionIcon variant="light" color={meta.color} size="lg" radius="md">
                        <Icon size={20} />
                      </ActionIcon>
                      <Text fw={600} size="sm">{meta.name}</Text>
                    </Group>
                  </Table.Td>
                  
                  <Table.Td>
                    <Text size="sm" c="dimmed">{meta.desc}</Text>
                  </Table.Td>
                  
                  <Table.Td style={{ textAlign: 'center' }}>
                    <Badge variant="dot" color="indigo" size="lg">
                      {agent.actionsToday.toLocaleString()}
                    </Badge>
                  </Table.Td>
                  
                  <Table.Td style={{ textAlign: 'center' }}>
                    {agent.enabled ? (
                      <Badge color="green" variant="light">Aktywny</Badge>
                    ) : (
                      <Badge color="gray" variant="light">Zatrzymany</Badge>
                    )}
                  </Table.Td>
                  
                  <Table.Td style={{ textAlign: 'right' }}>
                    <Button 
                      variant={agent.enabled ? "light" : "filled"} 
                      color={agent.enabled ? "red" : "green"}
                      size="xs"
                      radius="xl"
                      leftSection={agent.enabled ? <IconPlayerPause size={14} /> : <IconPlayerPlay size={14} />}
                      onClick={() => toggleAgent(agent.agentType, agent.enabled)}
                    >
                      {agent.enabled ? "Zatrzymaj" : "Uruchom"}
                    </Button>
                  </Table.Td>
                </Table.Tr>
              );
            })}
          </Table.Tbody>
        </Table>
      </Paper>
    </div>
  );
};

export default AgentManager;
